import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { StreamChat, Channel as StreamChannel } from "stream-chat";
import { useAuth } from "./use-auth";
import { customFetch } from "@workspace/api-client-react";

type ChatContextType = {
  client: StreamChat | null;
  isReady: boolean;
  isConfigured: boolean;
  apiKey: string | null;
};

const ChatContext = createContext<ChatContextType>({
  client: null,
  isReady: false,
  isConfigured: false,
  apiKey: null,
});

export function ChatProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [client, setClient] = useState<StreamChat | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [apiKey, setApiKey] = useState<string | null>(null);

  const initChat = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    try {
      const data = await customFetch<{ apiKey: string; token: string; userId: string; userName: string }>("/api/chat/token", {
        method: "POST",
      });
      setApiKey(data.apiKey);
      setIsConfigured(true);

      const chatClient = StreamChat.getInstance(data.apiKey);

      if (chatClient.userID) {
        await chatClient.disconnectUser();
      }

      await chatClient.connectUser(
        {
          id: data.userId,
          name: data.userName,
          image: user.avatarUrl || undefined,
        },
        data.token
      );

      setClient(chatClient);
      setIsReady(true);
    } catch (err) {
      console.error("Failed to initialize chat:", err);
      setIsConfigured(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    initChat();

    return () => {
      if (client) {
        client.disconnectUser().catch(console.error);
        setClient(null);
        setIsReady(false);
      }
    };
  }, [isAuthenticated, user?.id]);

  return (
    <ChatContext.Provider value={{ client, isReady, isConfigured, apiKey }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  return useContext(ChatContext);
}
